import { Badge, Button, Details, showToast } from "@emdur/ui";
import { type Agent, agentExpiry } from "../lib/agents";
import type { Client } from "../lib/clients";
import { PublicKeyIds } from "./PublicKeyIds";

/** The app settings rail also supplies an agent's linked client identity and public keys. */
export function RegistrySettings({
  client,
  agent,
  onError,
}: {
  client: Client | null;
  agent?: Agent;
  onError: (text: string) => void;
}) {
  const clientId = agent?.client_id ?? client?.client_id;
  const state = agent?.state ?? client?.state;
  return (
    <aside
      aria-label={agent ? "Agent settings" : "App settings"}
      className="min-w-0 space-y-6 rounded-panel bg-hover p-5"
    >
      {agent && (
        <Details
          items={[
            ["Agent id", <span className="break-all">{agent.id}</span>],
            [
              "Expiry",
              <time dateTime={agent.expires_at}>
                {agentExpiry(agent.expires_at)}
              </time>,
            ],
          ]}
        />
      )}
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-strong">
            {agent ? "Client id" : "App ID"}
          </h2>
          <Badge tone={state === "active" ? "positive" : "neutral"}>
            {state ? state[0]!.toUpperCase() + state.slice(1) : "Unavailable"}
          </Badge>
        </div>
        <p className="mb-2 break-all text-sm text-muted">
          {clientId ?? "No client id was returned."}
        </p>
        {clientId && (
          <Button
            size="sm"
            variant="quiet"
            onPress={async () => {
              try {
                await navigator.clipboard.writeText(clientId);
                showToast({
                  title: agent ? "Client id copied" : "App ID copied",
                  tone: "positive",
                });
              } catch {
                onError(
                  "The id could not be copied. Select it in settings to copy it.",
                );
              }
            }}
          >
            {agent ? "Copy client id" : "Copy app ID"}
          </Button>
        )}
        {agent && (
          <p className="mt-3 text-sm text-muted">
            Give this id to the product's release settings.
          </p>
        )}
      </div>
      <div>
        <h2 className="mb-3 text-sm font-strong">Keys</h2>
        {client?.jwks_uri ? (
          <Details
            items={[
              [
                "Public key URL",
                <span className="break-all">{client.jwks_uri}</span>,
              ],
              [
                "Health",
                "The Foundation does not yet report key-fetch health.",
              ],
            ]}
          />
        ) : client?.jwks ? (
          <>
            <p className="mb-2 text-sm text-muted">
              {client.jwks.keys.length} public{" "}
              {client.jwks.keys.length === 1 ? "key" : "keys"}
            </p>
            <PublicKeyIds keys={client.jwks.keys} />
          </>
        ) : (
          <p className="text-sm text-muted">
            {agent
              ? "Public keys are unavailable."
              : "This app uses device-bound sign-in and has no server key."}
          </p>
        )}
      </div>
      {!agent && client && (
        <div>
          <h2 className="mb-3 text-sm font-strong">Redirect addresses</h2>
          {client.redirect_uris.length ? (
            <ul className="space-y-2 text-sm">
              {client.redirect_uris.map((uri) => (
                <li className="break-all" key={uri}>
                  {uri}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">
              Services do not redirect people after sign-in.
            </p>
          )}
        </div>
      )}
    </aside>
  );
}
